import React from "react";
import ReactDOM from "react-dom/client";
import { createPortal } from "react-dom";
import "./index.css";
import "./select.css";
import AppRouter from "./AppRouter";
import reportWebVitals from "./reportWebVitals";
import { getApiUrl as getApiUrlUtil } from "./config/api";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

import { getAccessToken, setAccessToken } from "./utils/auth";

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

// Analytics wrapper component that renders at the bottom of body
function AnalyticsWrapper() {
  return createPortal(
    <>
      <Analytics />
      <SpeedInsights />
    </>,
    document.body
  );
}

root.render(
  <React.StrictMode>
    <AppRouter />
    <AnalyticsWrapper />
  </React.StrictMode>
);

// Global fetch wrapper to route API calls to backend
const originalFetch = window.fetch.bind(window);

function getApiUrl(path: string | URL): string | URL {
  if (typeof path !== "string") return path;
  // Use the centralized API URL utility
  if (path.startsWith("/")) {
    return getApiUrlUtil(path);
  }
  return path;
}

// Store original console methods to restore them later
const originalConsoleError = console.error;

// Override console.error to filter out expected 404 errors for invoice requests
console.error = function (...args: any[]) {
  // Check if this is a 404 error for an invoice request
  // Браузер выводит ошибки в формате: "GET http://localhost:3001/api/invoices/... 404 (Not Found)"
  const message = args.join(" ") || "";
  const isInvoice404Error =
    (message.includes("404") || message.includes("Not Found")) &&
    (message.includes("/api/invoices/") ||
      message.includes("localhost:3001/api/invoices/") ||
      message.includes("api/invoices"));

  // Suppress expected 404 errors for invoice requests
  if (isInvoice404Error) {
    // Don't log this error - it's expected when creating a new invoice
    return;
  }

  // Call original console.error for all other errors
  originalConsoleError.apply(console, args);
};

window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  // Extract URL safely for Vercel Analytics check
  let rawUrl = "";
  try {
    if (typeof input === "string") {
      rawUrl = input;
    } else if (input instanceof URL) {
      rawUrl = input.toString();
    } else if (input && typeof input === "object" && "url" in input) {
      rawUrl = (input as Request).url || "";
    }
  } catch (error) {
    // If URL extraction fails, proceed with normal fetch logic
  }

  // Allow Vercel Analytics and Speed Insights requests to pass through unchanged
  // These routes are handled by Vercel infrastructure and should not be modified
  // Note: Check without trailing slash to catch all variants (with/without slash, with query params)
  if (
    rawUrl.startsWith("/_vercel/insights") ||
    rawUrl.startsWith("/_vercel/speed-insights") ||
    rawUrl.startsWith("/_vercel") ||
    rawUrl.includes("vercel-insights.com") ||
    rawUrl.includes("vercel.com/_vercel") ||
    rawUrl.includes(".hot-update.") || // Don't intercept hot-update files
    (rawUrl.endsWith(".json") && !rawUrl.includes("/api/")) // Don't intercept static JSON files unless they are API calls
  ) {
    return originalFetch(input, init);
  }

  // Only prepend API URL for relative paths that are clearly API or auth calls
  const isApiPath =
    typeof input === "string" &&
    (input.startsWith("/api/") ||
      input.startsWith("/auth/") ||
      input.startsWith("/generate-pdf"));

  const target = isApiPath ? getApiUrl(input as any) : input;
  const url = typeof target === "string" ? target : target.toString();

  // Check if this is an external URL (not our backend)
  // External URLs are full HTTP/HTTPS URLs that don't point to our backend
  const isFullUrl = url.startsWith("http://") || url.startsWith("https://");
  const isRelativeUrl = url.startsWith("/");

  // For external URLs (like ECB API), use original fetch without credentials
  // This prevents CORS errors when external APIs return Access-Control-Allow-Origin: *
  if (
    isFullUrl &&
    !url.includes("localhost") &&
    !url.includes(window.location.hostname)
  ) {
    // Use original fetch without credentials for external APIs
    return originalFetch(target, {
      ...init,
      credentials: "omit", // Explicitly omit credentials for external APIs
    });
  }

  // Prepare headers for our backend requests
  const headers = new Headers(init?.headers);

  // Add Authorization header for API requests (but not for auth endpoints)
  if (
    (isRelativeUrl &&
      (url.startsWith("/api/") ||
        url.startsWith("/auth/") ||
        url.startsWith("/generate-pdf"))) ||
    (isFullUrl &&
      (url.includes("/api/") ||
        url.includes("/auth/") ||
        url.includes("/generate-pdf")))
  ) {
    if (!url.includes("/auth/")) {
      const token = getAccessToken();
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
    }
  }

  const newInit: RequestInit = {
    ...init,
    headers,
    credentials: "include",
  };

  try {
    const response = await originalFetch(target, newInit);

    // Handle 429 Too Many Requests
    if (response.status === 429) {
      window.dispatchEvent(new CustomEvent("rate-limit-exceeded"));
    }

    // Handle 401 Unauthorized - token expired or invalid
    if (response.status === 401 && (url.includes("/api/") || isRelativeUrl)) {
      // Clear invalid token
      setAccessToken(null);
      // Only redirect if we're not already on login/register page
      if (
        typeof window !== "undefined" &&
        !window.location.pathname.includes("/login")
      ) {
        // Don't redirect automatically - let components handle it
      }
    }

    return response;
  } catch (error) {
    // Re-throw network errors
    throw error;
  }
};

// Export auth utilities for other components
export {
  getAccessToken,
  setAccessToken,
  getUserData,
  setUserData,
  logout,
} from "./utils/auth";

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
